import { UserPreferences, GameMode, NotificationSettings } from '../types';

export class PreferencesManager {
  private static readonly STORAGE_KEY = 'tic-tac-toe-preferences';

  /**
   * Get user preferences from localStorage
   */
  getPreferences(): UserPreferences {
    try {
      const stored = localStorage.getItem(PreferencesManager.STORAGE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored);
        return this.validatePreferences(parsed);
      }
    } catch (error) {
      console.warn('Failed to load preferences from localStorage:', error);
    }
    
    return this.getDefaultPreferences();
  }

  /**
   * Save user preferences to localStorage
   */
  savePreferences(preferences: UserPreferences): void {
    try {
      const validated = this.validatePreferences(preferences);
      localStorage.setItem(PreferencesManager.STORAGE_KEY, JSON.stringify(validated));
    } catch (error) {
      console.error('Failed to save preferences to localStorage:', error);
      throw new Error('Failed to save preferences');
    }
  }

  /**
   * Update specific preference and save
   */
  updatePreference<K extends keyof UserPreferences>(
    key: K, 
    value: UserPreferences[K]
  ): UserPreferences {
    const currentPreferences = this.getPreferences();
    const updatedPreferences = {
      ...currentPreferences,
      [key]: value
    };
    
    this.savePreferences(updatedPreferences);
    return updatedPreferences;
  }

  /**
   * Clear all preferences (reset to defaults)
   */
  clearPreferences(): UserPreferences {
    try {
      localStorage.removeItem(PreferencesManager.STORAGE_KEY);
    } catch (error) {
      console.warn('Failed to clear preferences from localStorage:', error);
    }
    
    return this.getDefaultPreferences();
  }

  /**
   * Check if preferences exist in storage
   */
  hasStoredPreferences(): boolean {
    try {
      return localStorage.getItem(PreferencesManager.STORAGE_KEY) !== null;
    } catch (error) {
      return false;
    }
  }

  /**
   * Get default preferences
   */
  private getDefaultPreferences(): UserPreferences {
    return {
      theme: 'default',
      soundEnabled: true,
      defaultGameMode: GameMode.SINGLE_PLAYER,
      notifications: {
        turnBased: true,
        gameInvites: true,
        gameResults: true
      }
    };
  }

  /**
   * Validate and sanitize preferences object
   */
  private validatePreferences(preferences: any): UserPreferences {
    const defaults = this.getDefaultPreferences();
    
    // Ensure all required fields exist with proper types
    const validated: UserPreferences = {
      theme: typeof preferences.theme === 'string' ? preferences.theme : defaults.theme,
      soundEnabled: typeof preferences.soundEnabled === 'boolean' ? preferences.soundEnabled : defaults.soundEnabled,
      defaultGameMode: this.isValidGameMode(preferences.defaultGameMode) ? preferences.defaultGameMode : defaults.defaultGameMode,
      notifications: this.validateNotifications(preferences.notifications) || defaults.notifications
    };

    return validated;
  }

  /**
   * Validate game mode enum
   */
  private isValidGameMode(mode: any): mode is GameMode {
    return (Object.keys(GameMode) as Array<keyof typeof GameMode>)
      .some(key => GameMode[key] === mode);
  }

  /**
   * Validate notification settings
   */
  private validateNotifications(notifications: any): NotificationSettings | null {
    if (!notifications || typeof notifications !== 'object') {
      return null;
    }

    return {
      turnBased: typeof notifications.turnBased === 'boolean' ? notifications.turnBased : true,
      gameInvites: typeof notifications.gameInvites === 'boolean' ? notifications.gameInvites : true,
      gameResults: typeof notifications.gameResults === 'boolean' ? notifications.gameResults : true
    };
  }
}